import { useState } from 'react';
import { Icon } from '@iconify/react';
import combineClasses from 'classnames';
import { alpha3ToFlagEmoji } from '@peekapak/country-origin';
import { useHomeCountryResolution } from './useHomeCountryResolution';
import HomeCountrySelectionDialog from './HomeCountrySelectionDialog';

interface Props {
  /**
   * Whether resolving the country of origin may prompt for browser geolocation
   * (D14). Auth screens (sign-up/log-in) pass `true` (the default); marketing
   * contexts would pass `false`.
   */
  mayPromptForGeolocation?: boolean;
  className?: string;
}

/**
 * The home-country flag control shown on the sign-up and log-in screens. It
 * renders the resolved country of origin as a flag + name, and opens the
 * selection dialog so the user can see and correct the guess. When the country
 * is unknown (`UNK`, or a code with no flag) it shows a neutral placeholder
 * rather than a blank or wrong flag.
 */
export default function HomeCountryControl({ mayPromptForGeolocation = true, className }: Props): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { resolution, refresh } = useHomeCountryResolution(mayPromptForGeolocation);

  const countryOfOrigin = resolution?.countryOfOrigin ?? 'UNK';
  const countryName = resolution?.countryName ?? '';
  const flag = alpha3ToFlagEmoji(countryOfOrigin);
  const isUnknown = !flag;

  // The accessible name always carries the country (or the set-it prompt) so
  // screen-reader users get the meaning the flag emoji conveys visually (5.2).
  const label = isUnknown ? 'Set your home country' : `Home country: ${countryName}. Change.`;

  return (
    <>
      {/* Styled as a quiet region-picker line (like the "Not a member? Sign Up"
          epilogue): the page font must be set explicitly because buttons do not
          inherit font-family and this app disables Tailwind preflight. Sizes are
          arbitrary values because the app's 62.5% root font-size (1rem = 10px)
          shrinks Tailwind's stock rem-based utilities. */}
      <button
        type='button'
        onClick={() => {
          // Opening the control is the demand signal (D5): give detection
          // another chance (re-shows the prompt only if it was dismissed, or
          // silently succeeds if permission was re-enabled). The resolver
          // short-circuits on an explicit choice and never re-prompts after
          // denial; re-resolution never writes to storage.
          refresh();
          setIsDialogOpen(true);
        }}
        aria-label={label}
        aria-haspopup='dialog'
        className={combineClasses(
          'group inline-flex cursor-pointer appearance-none items-center gap-[0.6rem] rounded-full border border-solid border-peeka-grey bg-white px-[1.6rem] py-[0.8rem] text-[1.4rem] text-black transition-colors [font-family:GT-Walsheim-Pro-Light] hover:border-peeka-turquoise focus:outline-none focus-visible:ring-2 focus-visible:ring-peeka-turquoise',
          className,
        )}
      >
        <span aria-hidden='true' className='text-[1.6rem] leading-none'>
          {isUnknown ? <Icon icon='mdi:earth' width='16' height='16' /> : flag}
        </span>
        {isUnknown ? (
          <span aria-hidden='true' className='font-bold [font-family:GT-Walsheim-Pro-Bold] group-hover:underline'>
            Set home country
          </span>
        ) : (
          <span aria-hidden='true'>
            {countryName}
            <span className='font-bold [font-family:GT-Walsheim-Pro-Bold] group-hover:underline'>&nbsp;· Change</span>
          </span>
        )}
      </button>
      <HomeCountrySelectionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        resolution={resolution}
        onResolutionChange={refresh}
      />
    </>
  );
}
